import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTenant } from "../hooks/useTenant";
import { TenantSettingsEditor } from "../components/editor/TenantSettingsEditor";
import { useBillingSnapshots, type BillingSnapshot } from "../hooks/useBillingSnapshots";
import { useTenants } from "../hooks/useTenants";
import { InvoicesTable } from "../components/billing/InvoicesTable";
import { GenerateInvoiceModal } from "../components/billing/GenerateInvoiceModal";
import { ConfirmSendChargeDialog } from "../components/billing/ConfirmSendChargeDialog";
import { supabase } from "../lib/supabase";
import { colors, fontSize, fontWeight, radius, space } from "../lib/tokens";

type SubTab = "plano" | "faturas";

const SUB_TABS: { value: SubTab; label: string }[] = [
  { value: "plano", label: "Plano" },
  { value: "faturas", label: "Faturas" },
];

export function TenantBillingPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { tenant, loading: tenantLoading } = useTenant(slug);
  const [sub, setSub] = useState<SubTab>("plano");
  const [showGenModal, setShowGenModal] = useState(false);
  const [sendingSnapshot, setSendingSnapshot] = useState<BillingSnapshot | null>(null);

  const { snapshots, refetch, loading: snapshotsLoading } = useBillingSnapshots({
    tenantId: tenant?.id,
    limit: 50,
  });
  const { tenants } = useTenants();

  if (tenantLoading || !tenant) {
    return <div style={{ color: colors.stone }}>Carregando...</div>;
  }

  const onAction = async (action: string, s: BillingSnapshot) => {
    switch (action) {
      case "mark_paid": {
        if (!confirm("Marcar como paga?")) return;
        await supabase.functions.invoke("platform-mark-snapshot-paid", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "void": {
        if (!confirm("Anular esta fatura? (não cancela no Asaas)")) return;
        await supabase.functions.invoke("platform-void-snapshot", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "regenerate": {
        if (!confirm("Regenerar? (deleta + recria com counts atuais)")) return;
        await supabase.functions.invoke("platform-regenerate-snapshot", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "cancel": {
        if (!confirm("Cancelar cobrança no Asaas + anular?")) return;
        await supabase.functions.invoke("platform-cancel-charge", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "refund": {
        if (!confirm("Reembolsar fatura paga?")) return;
        await supabase.functions.invoke("platform-refund-charge", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "send": {
        setSendingSnapshot(s);
        return;
      }
    }
  };

  return (
    <div>
      <div style={subTabsBar}>
        {SUB_TABS.map((t) => {
          const isActive = sub === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setSub(t.value)}
              style={{
                padding: `${space.sm}px ${space.lg}px`,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                borderBottom: `2px solid ${isActive ? colors.ink : "transparent"}`,
                marginBottom: -1,
                color: isActive ? colors.ink : colors.stone,
                fontSize: fontSize.md,
                fontWeight: isActive ? fontWeight.semibold : fontWeight.medium,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {sub === "plano" && (
        <TenantSettingsEditor tenantId={tenant.id} section="billing" />
      )}

      {sub === "faturas" && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: space.md }}>
            <button onClick={() => setShowGenModal(true)} style={primaryBtn}>
              + Gerar fatura
            </button>
          </div>
          <InvoicesTable
            snapshots={snapshots}
            tenants={tenants}
            loading={snapshotsLoading}
            onAction={onAction}
          />
          {showGenModal && (
            <GenerateInvoiceModal
              tenants={tenants}
              defaultTenantId={tenant.id}
              onClose={() => setShowGenModal(false)}
              onCreated={() => refetch()}
            />
          )}
          {sendingSnapshot && (
            <ConfirmSendChargeDialog
              snapshot={sendingSnapshot}
              onClose={() => setSendingSnapshot(null)}
              onSent={() => refetch()}
            />
          )}
        </>
      )}
    </div>
  );
}

const subTabsBar: React.CSSProperties = {
  display: "flex",
  gap: space.xs,
  borderBottom: `1px solid ${colors.fog}`,
  marginBottom: space.lg,
  overflowX: "auto",
};

const primaryBtn: React.CSSProperties = {
  height: 36,
  padding: `0 ${space.md + 2}px`,
  background: colors.indigo,
  color: colors.white,
  border: "none",
  borderRadius: radius.sm,
  fontSize: fontSize.md,
  fontWeight: fontWeight.medium,
  cursor: "pointer",
};
